import { tokenMetaCache, type CachedMeta } from "./tokenMetaCache";

export interface PumpFunCoinResponse {
  mint: string;
  name: string;
  symbol: string;
  description: string;
  image_uri: string;
  video_uri?: string;
  metadata_uri: string;
  twitter?: string;
  telegram?: string;
  bonding_curve: string;
  associated_bonding_curve: string;
  creator: string;
  created_timestamp: number;
  raydium_pool: string | null;
  complete: boolean;
  virtual_sol_reserves: number;
  virtual_token_reserves: number;
  total_supply: number;
  website?: string;
  show_name: boolean;
  king_of_the_hill_timestamp?: number;
  forum_url?: string;
  is_currently_live: boolean;
  reply_count: number;
  last_reply: number;
}

const fetchPromises = new Map<string, Promise<void>>();

/**
 * Fetches extended narrative data from pump.fun for a given mint.
 * Dedupes inflight requests. Updates the tokenMetaCache upon success.
 */
export async function fetchNarrativeData(mint: string): Promise<void> {
  // If we already have the narrative fields (e.g. creator), we can skip.
  const cached = tokenMetaCache.get(mint);
  if (cached?.creator) {
    return;
  }

  if (fetchPromises.has(mint)) {
    return fetchPromises.get(mint)!;
  }

  const promise = (async () => {
    try {
      const res = await fetch(`/pump-frontend/coins/${mint}`);
      if (!res.ok) {
        throw new Error(`Failed to fetch narrative data: ${res.status}`);
      }
      
      const data: PumpFunCoinResponse = await res.json();
      
      const update: Partial<CachedMeta> & { mint: string } = {
        mint,
        name: data.name,
        symbol: data.symbol,
        imageUri: data.image_uri,
        description: data.description,
        twitter: data.twitter,
        telegram: data.telegram,
        website: data.website,
        creator: data.creator,
        replyCount: data.reply_count,
        createdTimestamp: data.created_timestamp,
      };
      
      tokenMetaCache.set(mint, update);
    } catch (err) {
      console.warn(`[NarrativeFetcher] Failed to fetch data for ${mint}`, err);
    } finally {
      fetchPromises.delete(mint);
    }
  })();

  fetchPromises.set(mint, promise);
  return promise;
}
